// src/services/data.ts
import type { Vaccine, DoseDefinition } from '../types';

/* =========================================================
   疫苗定義 (根據 vaccine_schedule 表格)
========================================================= */
export const VACCINES: Vaccine[] = [
  {
    id: 'hepB',
    name: 'B型肝炎疫苗',
    fullName: 'Hepatitis B Vaccine',
    type: 'public',
    description: '此疫苗主要用於降低B型肝炎及相關肝病風險。',
    sideEffects: ['接種後可能出現局部紅腫、輕微發燒或疲倦感。']
  },
  {
    id: 'hepB_ig',
    name: 'B型肝炎免疫球蛋白',
    fullName: 'Hepatitis B Immunoglobulin',
    type: 'public',
    description: '用於降低B型肝炎母嬰垂直感染風險。',
    sideEffects: ['可能出現注射處不適，較罕見有過敏反應。']
  },
  {
    id: '5in1',
    name: '白喉破傷風非細胞性百日咳、b型嗜血桿菌及不活化小兒麻痺五合一疫苗',
    fullName: 'DTaP-IPV-Hib',
    type: 'public',
    description: '用於降低白喉破傷風非細胞性百日咳、b型嗜血桿菌及不活化小兒麻痺。',
    sideEffects: ['可能出現發燒、哭鬧、局部紅腫或嗜睡。']
  },
  {
    id: '6in1',
    name: '白喉、破傷風、非細胞性百日咳、b型嗜血桿菌、不活化小兒麻痺及B型肝炎六合一疫苗',
    fullName: 'DTaP-IPV-Hib-HepB (Hexavalent)',
    type: 'self',
    description: '用於預防白喉、破傷風、百日咳、b型嗜血桿菌感染症、小兒麻痺及B型肝炎等六種疾病；可於出生滿6個月及18個月時自費接種，以減少針次並取代對應的五合一與B型肝炎劑次。',
    sideEffects: ['可能出現發燒、哭鬧、局部紅腫、嗜睡或疲倦感。']
  },
  {
    id: 'pcv13',
    name: '13價結合型肺炎鏈球菌疫苗',
    fullName: 'PCV13',
    type: 'public',
    description: '主要用於降低肺炎鏈球菌導致之肺炎、敗血症及腦膜炎等風險。',
    sideEffects: ['可能出現局部紅腫、發燒、嗜睡或煩躁。']
  },
  {
    id: 'rota',
    name: '輪狀病毒疫苗',
    fullName: 'Rotavirus Vaccine',
    type: 'self',
    description: '用於降低嬰幼兒嚴重輪狀病毒腸胃炎之風險。',
    sideEffects: ['可能出現腹瀉、嘔吐或煩躁；腸套疊為極罕見反應。']
  },
  {
    id: 'menB',
    name: 'B型腦膜炎球菌疫苗',
    fullName: 'Meningococcal B Vaccine',
    type: 'self',
    description: '降低由腦膜炎球菌 B 群引起嚴重感染（腦膜炎、敗血症）的風險。',
    sideEffects: ['可能出現發燒、局部紅腫或疲倦。']
  },
  {
    id: 'ev71',
    name: '腸病毒71型疫苗',
    fullName: 'Enterovirus 71 Vaccine',
    type: 'self',
    description: '主要用於降低腸病毒 A71 型引起之重症（如腦幹腦炎、心肺衰竭）風險。',
    sideEffects: ['可能出現局部紅腫、輕微發燒、疲倦或煩躁，屬常見反應。']
  },
  {
    id: 'flu',
    name: '流感疫苗',
    fullName: 'Influenza Vaccine',
    type: 'public',
    description: '用於降低季節性流感與重症風險。',
    sideEffects: ['可能出現局部腫脹、頭痛、倦怠或低燒。']
  },
  {
    id: 'bcg',
    name: '卡介苗',
    fullName: 'BCG',
    type: 'public',
    description: '用於降低結核病（尤其是小兒結核性腦膜炎）之風險。',
    sideEffects: ['常見反應包含局部潰瘍、結痂與腫脹。']
  },
  {
    id: 'mmr',
    name: '麻疹腮腺炎德國麻疹混合疫苗',
    fullName: 'MMR',
    type: 'public',
    description: '用於降低麻疹、腮腺炎與德國麻疹感染風險。',
    sideEffects: ['可能出現輕微發燒、皮疹或腮腺輕度腫脹。']
  },
  {
    id: 'varicella',
    name: '水痘疫苗',
    fullName: 'Varicella Vaccine',
    type: 'public',
    description: '用於降低水痘與其併發症（如肺炎、腦炎）之風險。',
    sideEffects: ['可能出現輕微皮疹、局部紅腫或發燒。']
  },
  {
    id: 'hepA',
    name: 'A型肝炎疫苗',
    fullName: 'Hepatitis A Vaccine',
    type: 'public',
    description: '用於降低A型肝炎及爆發性感染風險。',
    sideEffects: ['可能出現注射部位疼痛、倦怠或食慾下降。']
  },
  {
    id: 'je',
    name: '日本腦炎疫苗（活性減毒）',
    fullName: 'Japanese Encephalitis Vaccine (Live Attenuated)',
    type: 'public',
    description: '用於降低日本腦炎病毒感染風險。',
    sideEffects: ['可能出現局部紅腫或輕度發燒；嚴重過敏極為少見。']
  },
  {
    id: 'rsv',
    name: 'RSV 抗體注射（Beyfortus）',
    fullName: 'Nirsevimab (Beyfortus)',
    type: 'self',
    description: '直接注射長效型單株抗體，提供約 5 個月保護力，無需等待自身免疫反應。',
    sideEffects: ['局部紅腫、輕微發燒。'],
    notes: [
      '建議在 RSV 流行季（台灣秋冬 10月～隔年3月）前施打',
      '若媽媽孕期已接種 Abrysvo，建議在寶寶 2～4 個月大、母傳抗體消退後再施打',
      '保護力約持續 5 個月，通常每年施打一次',
    ]
  },
  {
    id: 'maternal_rsv',
    name: '孕期 RSV 疫苗（Abrysvo）',
    fullName: 'RSVpreF Vaccine (Abrysvo, Pfizer)',
    type: 'self',
    description: '孕婦於懷孕 24～36 週接種，透過胎盤將抗體傳遞給胎兒，出生後即具有對 RSV 的保護力，可持續約 6 個月。',
    sideEffects: ['注射部位疼痛、肌肉痠痛、頭痛、噁心。'],
    notes: [
      '建議接種時間：懷孕 24～36 週（最佳為 32～36 週）',
      '若已接種孕期 RSV 疫苗，寶寶出生後可延後施打 Beyfortus（建議 2～4 個月大再評估）',
      '目前為自費疫苗',
    ]
  },
  {
    id: 'pcv15',
    name: '15價結合型肺炎鏈球菌疫苗',
    fullName: 'PCV15 (Vaxneuvance)',
    type: 'self',
    description: '涵蓋 15 種血清型的結合型疫苗，比公費 13 價多保護 22F、33F 共 2 種血清型，且對致病性最高的血清型 3 具有更佳的免疫反應。適用於出生滿 6 週以上的嬰幼兒，採 3+1 時程（2、4、6、12-15 個月），全程自費接種。',
    sideEffects: [
      '注射部位疼痛、紅腫或硬塊',
      '發燒（≥38°C）、煩躁不安、嗜睡',
      '食慾下降',
      '早產兒接種後應留意呼吸暫停的可能性',
    ],
    notes: [
      '可從出生滿 2 個月起取代公費 13 價疫苗，全程自費接種',
      '或完成公費 13 價疫苗後，自費補打 1 劑 15 價擴大保護範圍',
      '對疫苗成分或白喉類毒素曾有嚴重過敏反應者不可接種',
      '確認接種 13 價、15 價或 20 價其中一方時，同時期的另一方會自動標為跳過',
    ]
  },
  {
    id: 'pcv20',
    name: '20價肺炎鏈球菌疫苗',
    fullName: 'PCV20 (Prevnar 20)',
    type: 'self',
    description: '涵蓋 20 種血清型的結合型疫苗，比公費 13 價多保護 8、10A、11A、12F、15B、22F、33F 共 7 種血清型，是目前涵蓋範圍最廣的嬰幼兒肺炎鏈球菌結合型疫苗。適用於出生滿 6 週以上的嬰幼兒，採 3+1 時程（2、4、6、12-15 個月），全程自費接種。',
    sideEffects: [
      '注射部位疼痛、紅腫或硬塊',
      '發燒（≥38°C）、煩躁不安、嗜睡',
      '食慾下降',
      '早產兒接種後應留意呼吸暫停的可能性',
    ],
    notes: [
      '可從出生滿 2 個月起取代公費 13 價疫苗，全程自費接種',
      '或完成公費 13 價疫苗後，自費補打 1 劑 20 價擴大保護範圍',
      '對疫苗成分或白喉類毒素曾有嚴重過敏反應者不可接種',
      '確認接種 13 價、15 價或 20 價其中一方時，同時期的另一方會自動標為跳過',
    ]
  },
  {
    id: 'ppv23',
    name: '23價多醣體肺炎鏈球菌疫苗',
    fullName: 'PPV23 (Pneumovax 23)',
    type: 'self',
    description: '涵蓋 23 種血清型的多醣體疫苗，雖然涵蓋的血清型數量最多，但屬於較舊的技術，無法激發長效免疫記憶，保護力約 3～5 年。此疫苗不適用於 2 歲以下嬰幼兒，僅用於 2 歲以上經醫師評估為高風險的兒童，作為結合型疫苗完成後的追加補強。',
    sideEffects: [
      '注射部位疼痛、紅腫、硬塊或局部發熱',
      '發燒、疲倦、頭痛',
      '肌肉痠痛、關節痛',
      '較罕見可能出現淋巴結腫大、皮疹或蕁麻疹',
    ],
    notes: [
      '僅適用於滿 2 歲以上且經醫師評估為高風險族群的兒童（如無脾臟、先天免疫不全、慢性心肺疾病、植入人工耳蝸等）',
      '必須先完成結合型疫苗（13 價、15 價或 20 價）後，間隔至少 8 週才可接種',
      '2 歲以下嬰幼兒免疫系統尚未成熟，無法對多醣體疫苗產生足夠抗體，不可接種',
      '若先打了此疫苗再打結合型疫苗，可能產生免疫鈍化，短期內結合型疫苗的效果會變差',
      '對疫苗成分曾有嚴重過敏反應者不可接種',
    ]
  }
];

/* =========================================================
   劑次定義
========================================================= */
export const DOSES: DoseDefinition[] = [
  // B型肝炎免疫球蛋白
  {
    id: 'hepB_ig-1',
    vaccineId: 'hepB_ig',
    doseIndex: 1,
    label: '單劑',
    intervalFromBirth: { days: 0 },
    customAgeLabel: '出生24小時內'
  },

  // B型肝炎疫苗
  {
    id: 'hepB-1',
    vaccineId: 'hepB',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { days: 0 },
    customAgeLabel: '出生24小時內',
    minAgeFromBirth: { days: 0 },
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑最小接種年齡：出生24小時內儘速', checkedAt: '2026-08-25' }
  },
  {
    id: 'hepB-2',
    vaccineId: 'hepB',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 1 },
    customAgeLabel: '出生滿1個月',
    minIntervals: [{ fromDoseId: 'hepB-1', days: 28 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: 'hepB-3',
    vaccineId: 'hepB',
    doseIndex: 3,
    label: '第3劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['6in1-1'], onlyIfStatus: ['planned', 'overdue'], reason: '六合一疫苗可取代同時期的五合一第3劑與B型肝炎第3劑' }],
    minIntervals: [
      { fromDoseId: 'hepB-2', days: 56 },
      { fromDoseId: 'hepB-1', days: 112 }
    ],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第2劑與第3劑間隔至少8週，且第1劑與第3劑間隔至少16週', checkedAt: '2026-08-25' }
  },

  // 五合一疫苗
  {
    id: '5in1-1',
    vaccineId: '5in1',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    minAgeFromBirth: { days: 42 },
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑最小接種年齡：6週', checkedAt: '2026-08-25' }
  },
  {
    id: '5in1-2',
    vaccineId: '5in1',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    minIntervals: [{ fromDoseId: '5in1-1', days: 28 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: '5in1-3',
    vaccineId: '5in1',
    doseIndex: 3,
    label: '第3劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['6in1-1'], onlyIfStatus: ['planned', 'overdue'], reason: '六合一疫苗可取代同時期的五合一第3劑與B型肝炎第3劑' }],
    minIntervals: [{ fromDoseId: '5in1-2', days: 28 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第2劑與第3劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: '5in1-4',
    vaccineId: '5in1',
    doseIndex: 4,
    label: '第4劑',
    intervalFromBirth: { months: 18 },
    customAgeLabel: '出生滿18個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['6in1-2'], onlyIfStatus: ['planned', 'overdue'], reason: '六合一疫苗可取代同時期的五合一第4劑' }],
    minIntervals: [{ fromDoseId: '5in1-3', days: 180 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第3劑與第4劑間隔至少6個月，且須滿1歲後接種', checkedAt: '2026-08-25' }
  },
  {
    id: '5in1-5',
    vaccineId: '5in1',
    doseIndex: 5,
    label: '第5劑',
    intervalFromBirth: { days: 1825 },
    customAgeLabel: '滿5歲至入國小前'
  },
  // 六合一疫苗
  {
    id: '6in1-1',
    vaccineId: '6in1',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['5in1-3', 'hepB-3'], onlyIfStatus: ['planned', 'overdue'], reason: '六合一疫苗已涵蓋五合一第3劑與B型肝炎第3劑成分' }]
  },
  {
    id: '6in1-2',
    vaccineId: '6in1',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 18 },
    customAgeLabel: '出生滿18個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['5in1-4'], onlyIfStatus: ['planned', 'overdue'], reason: '六合一疫苗已涵蓋五合一第4劑成分' }]
  },
  // 13價肺炎鏈球菌疫苗
  {
    id: 'pcv13-1',
    vaccineId: 'pcv13',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv15-d1', 'pcv20-d1'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    minAgeFromBirth: { days: 42 },
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑最小接種年齡：6週', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv13-2',
    vaccineId: 'pcv13',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv15-d2', 'pcv20-d2'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    minIntervals: [{
      fromDoseId: 'pcv13-1',
      days: 56,
      conditionalOverrides: [{ ifAgeAtPreviousDoseAtLeastMonths: 7, days: 28 }]
    }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少8週；若第1劑於滿7個月後才開始接種，間隔可縮短為4週', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv13-s3',
    vaccineId: 'pcv13',
    doseIndex: 4,
    label: '追加劑',
    type: 'self',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv15-d3', 'pcv20-d4'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }]
  },
  {
    id: 'pcv13-3',
    vaccineId: 'pcv13',
    doseIndex: 3,
    label: '第3劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12-15個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv15-d4', 'pcv20-d3'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    minIntervals: [{ fromDoseId: 'pcv13-2', days: 56 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第2劑與第3劑間隔至少8週，且須滿1歲後接種', checkedAt: '2026-08-25' }
  },

  // 輪狀病毒疫苗
  {
    id: 'rota-1',
    vaccineId: 'rota',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    sourceRef: { doc: '疾管署 輪狀病毒疫苗接種時程', section: '第1劑最早之接種年齡為出生滿6週', checkedAt: '2026-08-25' }
  },
  {
    id: 'rota-2',
    vaccineId: 'rota',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    notes: ['若選擇2劑型（Rotarix），本劑為最後一劑，不得晚於出生後24週接種'],
    minIntervals: [{ fromDoseId: 'rota-1', days: 28 }],
    sourceRef: { doc: '疾管署 輪狀病毒疫苗接種時程', section: '2劑時程：出生滿2、4個月，間隔至少4週；最後1劑不得晚於出生後24週接種', checkedAt: '2026-08-25' }
  },
  {
    id: 'rota-3',
    vaccineId: 'rota',
    doseIndex: 3,
    label: '第3劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    notes: ['3劑型（RotaTeq）最後一劑不得晚於出生後32週接種'],
    minIntervals: [{ fromDoseId: 'rota-2', days: 28 }],
    sourceRef: { doc: '疾管署 輪狀病毒疫苗接種時程', section: '3劑時程：出生滿2、4、6個月，間隔至少4週；最後1劑不得晚於出生後32週接種', checkedAt: '2026-08-25' }
  },

  // B型腦膜炎球菌疫苗
  {
    id: 'menB-1',
    vaccineId: 'menB',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    sourceRef: { doc: '衛部菌疫輸字第001150號 必思諾(Bexsero)仿單', section: '劑量與用法：嬰兒2-5個月大，基礎免疫接種2劑', checkedAt: '2026-08-25' }
  },
  {
    id: 'menB-2',
    vaccineId: 'menB',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    notes: ['與第1劑至少間隔2個月'],
    sourceRef: { doc: '衛部菌疫輸字第001150號 必思諾(Bexsero)仿單', section: '劑量與用法：施打基礎劑的間隔時間不少於2個月', checkedAt: '2026-08-25' }
  },
  {
    id: 'menB-3',
    vaccineId: 'menB',
    doseIndex: 3,
    label: '追加劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12-15個月',
    maxAgeFromBirth: { months: 24 },
    notes: ['與基礎系列（第2劑）至少間隔6個月', '追加劑施打不應晚於24個月大之後'],
    sourceRef: { doc: '衛部菌疫輸字第001150號 必思諾(Bexsero)仿單', section: '劑量與用法：應於12到15個月大時施打追加劑', checkedAt: '2026-08-25' }
  },

  // 腸病毒71型疫苗
  {
    id: 'ev71-1',
    vaccineId: 'ev71',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月'
  },
  {
    id: 'ev71-2',
    vaccineId: 'ev71',
    doseIndex: 2,
    label: '第2劑',
    intervalFromDose: { fromDoseIndex: 1, months: 2, label: '第1劑後2個月' },
    customAgeLabel: '第1劑後2個月'
  },
  {
    id: 'ev71-3',
    vaccineId: 'ev71',
    doseIndex: 3,
    label: '追加劑',
    intervalFromDose: { fromDoseIndex: 1, months: 12, label: '第1劑後1年' },
    customAgeLabel: '第1劑後1年'
  },

  // 流感疫苗 (每年一劑 + 初次第2劑)
  {
    id: 'flu-y1-1',
    vaccineId: 'flu',
    doseIndex: 1,
    label: '第1年第1劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '滿6個月至12個月'
  },
  {
    id: 'flu-y1-2',
    vaccineId: 'flu',
    doseIndex: 2,
    label: '第1年第2劑',
    intervalFromDose: { fromDoseIndex: 1, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y2-1',
    vaccineId: 'flu',
    doseIndex: 3,
    label: '第2年第1劑',
    intervalFromBirth: { days: 545 },
    customAgeLabel: '滿1歲至2歲'
  },
  {
    id: 'flu-y2-2',
    vaccineId: 'flu',
    doseIndex: 4,
    label: '第2年第2劑',
    intervalFromDose: { fromDoseIndex: 3, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y3-1',
    vaccineId: 'flu',
    doseIndex: 5,
    label: '第3年第1劑',
    intervalFromBirth: { days: 730 },
    customAgeLabel: '滿2歲至3歲'
  },
  {
    id: 'flu-y3-2',
    vaccineId: 'flu',
    doseIndex: 6,
    label: '第3年第2劑',
    intervalFromDose: { fromDoseIndex: 5, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y4-1',
    vaccineId: 'flu',
    doseIndex: 7,
    label: '第4年第1劑',
    intervalFromBirth: { days: 1095 },
    customAgeLabel: '滿3歲至4歲'
  },
  {
    id: 'flu-y4-2',
    vaccineId: 'flu',
    doseIndex: 8,
    label: '第4年第2劑',
    intervalFromDose: { fromDoseIndex: 7, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y5-1',
    vaccineId: 'flu',
    doseIndex: 9,
    label: '第5年第1劑',
    intervalFromBirth: { days: 1460 },
    customAgeLabel: '滿4歲至5歲'
  },
  {
    id: 'flu-y5-2',
    vaccineId: 'flu',
    doseIndex: 10,
    label: '第5年第2劑',
    intervalFromDose: { fromDoseIndex: 9, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y6-1',
    vaccineId: 'flu',
    doseIndex: 11,
    label: '第6年第1劑',
    intervalFromBirth: { days: 1825 },
    customAgeLabel: '滿5歲至6歲'
  },
  {
    id: 'flu-y6-2',
    vaccineId: 'flu',
    doseIndex: 12,
    label: '第6年第2劑',
    intervalFromDose: { fromDoseIndex: 11, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },
  {
    id: 'flu-y7-1',
    vaccineId: 'flu',
    doseIndex: 13,
    label: '第7年第1劑',
    intervalFromBirth: { days: 2190 },
    customAgeLabel: '滿6歲至入國小前'
  },
  {
    id: 'flu-y7-2',
    vaccineId: 'flu',
    doseIndex: 14,
    label: '第7年第2劑',
    intervalFromDose: { fromDoseIndex: 13, gapDays: 28 },
    customAgeLabel: '第1劑後4週'
  },

  // 卡介苗
  {
    id: 'bcg-1',
    vaccineId: 'bcg',
    doseIndex: 1,
    label: '單劑',
    intervalFromBirth: { months: 5 },
    customAgeLabel: '出生滿5-8個月'
  },
  {
    id: 'bcg-補',
    vaccineId: 'bcg',
    doseIndex: 2,
    label: '補種',
    intervalFromBirth: { days: 2190 },
    customAgeLabel: '國小一年級'
  },

  // MMR
  {
    id: 'mmr-1',
    vaccineId: 'mmr',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12個月'
  },
  {
    id: 'mmr-2',
    vaccineId: 'mmr',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { days: 1825 },
    customAgeLabel: '滿5歲至入國小前',
    minIntervals: [{ fromDoseId: 'mmr-1', days: 28 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少4週', checkedAt: '2026-08-25' }
  },

  // 水痘疫苗
  {
    id: 'varicella-1',
    vaccineId: 'varicella',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12個月'
  },
  {
    id: 'varicella-2',
    vaccineId: 'varicella',
    doseIndex: 2,
    label: '第2劑',
    type: 'self',
    intervalFromBirth: { days: 1460 },
    customAgeLabel: '滿4歲至6歲'
  },

  // A型肝炎疫苗
  {
    id: 'hepA-1',
    vaccineId: 'hepA',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 18 },
    customAgeLabel: '出生滿18個月',
    minAgeFromBirth: { days: 360 },
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑最小接種年齡：12個月', checkedAt: '2026-08-25' }
  },
  {
    id: 'hepA-2',
    vaccineId: 'hepA',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 27 },
    customAgeLabel: '出生滿27個月',
    minIntervals: [{ fromDoseId: 'hepA-1', days: 180 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少6個月', checkedAt: '2026-08-25' }
  },

  // 日本腦炎疫苗
  {
    id: 'je-1',
    vaccineId: 'je',
    doseIndex: 1,
    label: '第1劑',
    intervalFromBirth: { months: 15 },
    customAgeLabel: '出生滿15個月'
  },
  {
    id: 'je-2',
    vaccineId: 'je',
    doseIndex: 2,
    label: '第2劑',
    intervalFromBirth: { months: 27 },
    customAgeLabel: '出生滿27個月',
    minIntervals: [{ fromDoseId: 'je-1', days: 360 }],
    sourceRef: { doc: '疾管署 現行預防接種時程表與間隔補種指引', section: '第1劑與第2劑間隔至少12個月（活性減毒劑型）', checkedAt: '2026-08-25' }
  },

  // RSV 抗體注射（Beyfortus）
  {
    id: 'rsv-1',
    vaccineId: 'rsv',
    doseIndex: 1,
    label: '單劑',
    intervalFromBirth: { days: 0 }, // 佔位用，實際日期由 calculateBeyfortusDate 計算
    flexibleTiming: true,
    customAgeLabel: '依流行季評估施打時機'
  },
  {
    id: 'rsv-2',
    vaccineId: 'rsv',
    doseIndex: 2,
    label: '再次給藥',
    intervalFromDose: { fromDoseIndex: 1, months: 6, label: '前一劑後至少6個月，且進入下一個RSV流行季' },
    scheduleRule: {
      version: 1,
      type: 'anchorOffset',
      anchor: { type: 'dose', doseIndex: 1 },
      offset: { months: 6, mode: 'minimum' },
      alignTo: { type: 'seasonWindow', startMonth: 10, startDay: 1, endMonth: 3, endDay: 31, ifOutside: 'nextWindowStart' }
    },
    legacyCompatible: false,
    customAgeLabel: '高風險幼兒第二個RSV流行季',
    notes: ['僅適用第二個 RSV 流行季仍符合適應症的高風險幼兒，請由醫師評估。']
  },

  // 20價肺炎鏈球菌疫苗
  {
    id: 'pcv20-d1',
    vaccineId: 'pcv20',
    doseIndex: 2,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-1', 'pcv15-d1'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }]
  },
  {
    id: 'pcv20-d2',
    vaccineId: 'pcv20',
    doseIndex: 3,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-2', 'pcv15-d2'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }]
  },
  {
    id: 'pcv20-d4',
    vaccineId: 'pcv20',
    doseIndex: 5,
    label: '第3劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-s3', 'pcv15-d3'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }]
  },
  {
    id: 'pcv20-d3',
    vaccineId: 'pcv20',
    doseIndex: 4,
    label: '追加劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-3', 'pcv15-d4'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }]
  },
  {
    id: 'pcv20-1',
    vaccineId: 'pcv20',
    doseIndex: 1,
    label: '補充劑',
    intervalFromBirth: { months: 15 },
    customAgeLabel: '出生滿15個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv15-1'], onlyIfStatus: ['planned', 'overdue'], reason: 'PCV15 與 PCV20 補充劑擇一接種' }]
  },

  // 15價結合型肺炎鏈球菌疫苗
  {
    id: 'pcv15-d1',
    vaccineId: 'pcv15',
    doseIndex: 2,
    label: '第1劑',
    intervalFromBirth: { months: 2 },
    customAgeLabel: '出生滿2個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-1', 'pcv20-d1'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    sourceRef: { doc: '次級來源（醫療機構衛教網站，多篇一致），未取得台灣仿單（Vaxneuvance／肺恩賜）原文核對——查詢平台需驗證碼，暫無法取得', section: '2、4、6個月接種基礎劑，基礎劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv15-d2',
    vaccineId: 'pcv15',
    doseIndex: 3,
    label: '第2劑',
    intervalFromBirth: { months: 4 },
    customAgeLabel: '出生滿4個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-2', 'pcv20-d2'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    sourceRef: { doc: '次級來源（醫療機構衛教網站，多篇一致），未取得台灣仿單（Vaxneuvance／肺恩賜）原文核對——查詢平台需驗證碼，暫無法取得', section: '2、4、6個月接種基礎劑，基礎劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv15-d3',
    vaccineId: 'pcv15',
    doseIndex: 4,
    label: '第3劑',
    intervalFromBirth: { months: 6 },
    customAgeLabel: '出生滿6個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-s3', 'pcv20-d4'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    sourceRef: { doc: '次級來源（醫療機構衛教網站，多篇一致），未取得台灣仿單（Vaxneuvance／肺恩賜）原文核對——查詢平台需驗證碼，暫無法取得', section: '2、4、6個月接種基礎劑，基礎劑間隔至少4週', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv15-d4',
    vaccineId: 'pcv15',
    doseIndex: 5,
    label: '追加劑',
    intervalFromBirth: { months: 12 },
    customAgeLabel: '出生滿12-15個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv13-3', 'pcv20-d3'], onlyIfStatus: ['planned', 'overdue'], reason: '同時期 PCV13、PCV15 與 PCV20 擇一接種' }],
    sourceRef: { doc: '次級來源（醫療機構衛教網站，多篇一致），未取得台灣仿單（Vaxneuvance／肺恩賜）原文核對——查詢平台需驗證碼，暫無法取得', section: '追加劑於12-15個月接種', checkedAt: '2026-08-25' }
  },
  {
    id: 'pcv15-1',
    vaccineId: 'pcv15',
    doseIndex: 1,
    label: '補充劑',
    intervalFromBirth: { months: 15 },
    customAgeLabel: '出生滿15個月',
    completionRules: [{ version: 1, type: 'skipDoses', targetDoseIds: ['pcv20-1'], onlyIfStatus: ['planned', 'overdue'], reason: 'PCV15 與 PCV20 補充劑擇一接種' }],
    sourceRef: { doc: '次級來源（醫療機構衛教網站，多篇一致），未取得台灣仿單（Vaxneuvance／肺恩賜）原文核對——查詢平台需驗證碼，暫無法取得', section: '1歲以前未完整接種者，滿15個月後補接種1劑', checkedAt: '2026-08-25' }
  },

  // 23價多醣體肺炎鏈球菌疫苗
  {
    id: 'ppv23-1',
    vaccineId: 'ppv23',
    doseIndex: 1,
    label: '單劑',
    intervalFromBirth: { months: 24 },
    customAgeLabel: '滿2歲以上（高風險族群）'
  }
];

/* =========================================================
   快速查詢 Map
========================================================= */
export const VACCINE_MAP = Object.fromEntries(
  VACCINES.map(v => [v.id, v])
) as Record<string, Vaccine>;

export const DOSE_MAP = Object.fromEntries(
  DOSES.map(d => [d.id, d])
) as Record<string, DoseDefinition>;

export function getVaccineById(vaccineId: string): Vaccine | undefined {
  return VACCINE_MAP[vaccineId];
}

export function getDoseById(doseId: string): DoseDefinition | undefined {
  return DOSE_MAP[doseId];
}

export function getDosesByVaccineId(vaccineId: string): DoseDefinition[] {
  return DOSES.filter(d => d.vaccineId === vaccineId);
}

export function getTotalDosesCount(vaccineId: string): number {
  return DOSES.filter(d => d.vaccineId === vaccineId).length;
}
